import * as React from 'react'

import { buildButtonVariantProps } from '~ui/components/buttons/Button/buildButtonVariantProps'
import { Stack } from '~ui/components/layout/Stack'
import { Skeleton } from '~ui/components/loadingSkeletons/Skeleton'
import { Text } from '~ui/components/typography/Text/Text'

import { ROLLING_OPTIONS_ARROW_HEIGHT, RollingOptionsButton } from './RollingOptionsButton'

type TButtonProps = Pick<
  PropsOf<typeof RollingOptionsButton>,
  'radius' | 'padding' | 'shadow' | 'shadowInset' | 'variant' | 'direction'
>

type TRollingOptionsItem = {
  id: string
  label: string
}

type TProps = NoChildren &
  SkeletonProps &
  TestingProps &
  TButtonProps & {
    /**
     * intended to be set as the id (HTMLElement id attribute) of the direct parent of rendered RollingOptionsButtons (it is used to identify the parent container, for example in ScrollOverflow)
     */
    optionsContainerId?: string
    /**
     * background color of unselected items
     */
    bgColor?: PropsOf<typeof RollingOptionsButton>['bgColor']
    /**
     * max width of the stack (vertical) or individual buttons (horizontal)
     */
    maxWidth?: string
    /**
     * id of the selected item – used to recolor it & add arrow
     */
    selectedId: string
    /**
     * background color od selected item
     */
    selectedBgColor?: PropsOf<typeof RollingOptionsButton>['bgColor']
    /**
     * data for rendering the buttons
     */
    items: RoA<TRollingOptionsItem>
    /**
     * called on button click
     */
    onSelection: (selectedId: string) => void
    /**
     * key of predefined spaces, defines the space between items in the Stack
     */
    spacing?: PropsOf<typeof Stack>['spacing']
  }

export const generatePlaceholderRollingOptionsItems = (itemCount: number) => {
  const placeholderItems: RoA<TRollingOptionsItem> = Array.from(Array(itemCount), (_, index) => {
    return {
      id: `${index}`,
      label: `description placeholder`,
    }
  })

  return placeholderItems
}

const SHADOW_OVERFLOW_PADDING = `2px`

export const RollingOptions: React.FC<TProps> = ({
  optionsContainerId,
  showSkeleton = false,
  items,
  maxWidth,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  children, // just taking this out of the buttonProps
  selectedId,
  onSelection,
  bgColor = `tertiary`,
  radius,
  spacing = `medium`,
  selectedBgColor = `primary`,
  variant,
  dataTestId,
  ...buttonProps
}) => {
  const { direction } = buttonProps

  // for vertical stack, make all of the items the same width
  // for horizontal stack, set maxWidth directly to each item
  const stackWidth = direction === `column` ? maxWidth : `100%`
  const itemWidth = direction === `column` ? `100%` : `auto`
  const itemMaxWidth = direction === `row` ? maxWidth : undefined

  // make some space for the selected item arrow
  const containerPadding =
    direction === `column`
      ? `${SHADOW_OVERFLOW_PADDING} ${ROLLING_OPTIONS_ARROW_HEIGHT} ${SHADOW_OVERFLOW_PADDING} ${SHADOW_OVERFLOW_PADDING}`
      : `${SHADOW_OVERFLOW_PADDING} ${SHADOW_OVERFLOW_PADDING} ${ROLLING_OPTIONS_ARROW_HEIGHT} ${SHADOW_OVERFLOW_PADDING}`

  return (
    <Stack
      spacing={spacing}
      width={stackWidth}
      maxWidth={`100%`}
      direction={direction}
      id={optionsContainerId}
      _padding={containerPadding}
    >
      {items.map(({ id, label }) => {
        const isSelected = id === selectedId

        return (
          <Skeleton showSkeleton={showSkeleton} key={id}>
            <RollingOptionsButton
              {...buttonProps}
              dataTestId={dataTestId}
              radius={radius}
              width={itemWidth}
              maxWidth={itemMaxWidth}
              withArrow={isSelected}
              onClick={() => onSelection(id)}
              shadow={isSelected ? `none` : buttonProps.shadow}
              shadowInset={isSelected ? undefined : buttonProps.shadowInset}
              {...buildButtonVariantProps({
                variant: isSelected ? `solid` : variant,
                bgColor: isSelected ? selectedBgColor : bgColor,
              })}
            >
              <Text
                width={`100%`}
                fontSize={`m16`}
                display={`block`}
                textAlign={`left`}
                truncateOnLines={1}
                fontStyle={`bold`}
                contrast={isSelected ? `high` : `normal`}
              >
                {label}
              </Text>
            </RollingOptionsButton>
          </Skeleton>
        )
      })}
    </Stack>
  )
}
